<template>
  <div class="nav-menu">
    <div class="nav-menu__group">
      <!-- TITLE -->
      <h5>
        <span>{{ title }}</span>
        <slot name="title-right" />
      </h5>

      <!-- OPTIONS -->
      <div
        v-for="option in elements"
        :key="option.value"
        :class="{ 'nav-menu__group-option--active' : modelValue == option.value }"
        class="nav-menu__group-option"
        @click="$emit('update:modelValue', option.value)"
      >
        <!-- LEFT ICON -->
        <span
          v-if="option.icon"
          class="nav-menu__group-option-icon nav-menu__group-option-icon--left"
        >
          <i :class="'ddi-'+option.icon" class="ddi" />
        </span>

        <span class="nav-menu__group-option-text">{{ option.label }}</span>

        <!-- COUNTERS -->
        <div
          v-if="option.hasOwnProperty('count')"
          class="nav-menu__group-option-count"
        >{{ getFilterCount(option) }}</div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { INavMenuItem } from '@/types/data-objects/global'

export default defineComponent({
  name: 'NavMenu',

  props: {
    modelValue: {
      type: [String, Number],
      default: '',
    },
    elements: {
      type: Array as () => INavMenuItem[],
      required: true,
    },
    title: {
      type: String,
      default: '',
    },
  },

  emits: ['update:modelValue'],

  methods: {
    getFilterCount(item: INavMenuItem) {
      if (item.count !== null) {
        if (item.count > 999) {
          return '999+'
        }
        return item.count
      }
      return ''
    },
  },
})
</script>

<style lang="scss">
.nav-menu {
  &__group:not(:first-child) {
    margin-top: 12px;
  }

  h5 {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 12px;
  }

  &__group-option {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 32px;
    padding: 8px 12px;
    margin-bottom: 4px;
    color: var(--color-text-secondary);
    cursor: pointer;
    border-radius: 4px;

    &:hover {
      background-color: rgba($color--gray-60, 0.12);
    }
  }

  &__group-option-text,
  &__group-option-count {
    @include lettering--p;
  }

  &__group-option-text {
    @include single-line-ellipsis;
    flex-grow: 1;
    padding-right: 30px;
    color: var(--color-text-secondary);
  }

  &__group-option-count {
    color: var(--color-text-third);
  }

  &__group-option-icon {
    position: relative;

    .ddi {
      top: -1px;
      color: var(--color-text-third);
    }

    &--left {
      height: initial;
      margin-right: 8px;
    }

    &--right {
      display: flex;
      flex-direction: row;
      align-self: end;
      margin-left: auto;
      color: var(--color-text-third);

      .ddi:first-of-type,
      i:first-of-type {
        margin-right: 8px;
      }
    }

    &--active {
      position: absolute;
      top: 50% !important;
      right: 18px;
      color: white;
      transform: translateY(-50%);
    }

    &--editable {
      display: flex;

      .ddi {
        align-items: center;
        font-size: 16px;
        line-height: 16px;
        opacity: 0.64;

        &:hover {
          opacity: 1;
        }
      }
    }
  }

  &__group--active {
    background-color: $color--blue;

    .nav-menu__group-option-text,
    .nav-menu__group-option-count {
      font-weight: 500;
      color: var(--color-general-white);
    }

    .nav-menu__group-option-icon {
      color: var(--color-general-white);
    }
  }

  &__group-option--active {
    background: $color--blue;

    .nav-menu__group-option-icon .ddi {
      color: var(--color-general-white);
      opacity: 0.64;
    }

    .nav-menu__group-option-count,
    .nav-menu__group-option-text {
      font-weight: 500;
      color: var(--color-general-white);
    }

    &:hover {
      background-color: $color--blue;
    }
  }
}
</style>
